import { zodResolver } from '@hookform/resolvers/zod'
import { ChevronsDown, ChevronsUp } from 'lucide-react'
import React from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useSendReferralStore } from 'src/features/business/referrals/stores/send-referral-store'
import { sendEvent } from 'src/shared/api/events/send-event'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { InputFormField } from 'src/shared/components/ui/forms/form-fields/input'
import { PriceRangeFormField } from 'src/shared/components/ui/forms/form-fields/price-range'
import { SelectFormField } from 'src/shared/components/ui/forms/form-fields/select'
import { TextareaFormField } from 'src/shared/components/ui/forms/form-fields/textarea'
import { FixedFooter } from 'src/shared/components/ui/miscellaneous/fixed-footer'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { propertyTypeSchema, propertyTypeMap, projectTypeSchema, projectTypeMap } from 'src/shared/schemas/referrals'
import { timeframeMap, timeframeSchema } from 'src/shared/schemas/referrals/referral-fields/shared/timeframe'
import { useEventStore } from 'src/shared/store/event-store'

const contractorSchema = z.object({
  projectType: projectTypeSchema,
  propertyType: propertyTypeSchema,
  budget: z.object({
    min: z.number().min(0),
    max: z.number().min(0),
  }),
  // Optional fields
  squareFootageEstimate: z.number().optional(),
  descriptionOfWork: z.string().max(1000).optional(),
  materialPreferences: z.string().max(1000).optional(),
  timeframe: timeframeSchema.optional(),
})

type ContractorFormData = z.infer<typeof contractorSchema>

export const ContractorInfo: React.FC = () => {
  const handleNext = useSendReferralStore((state) => state.handleNext)
  const handleBack = useSendReferralStore((state) => state.handleBack)
  const selectedReferral = useSendReferralStore((state) => state.selectedReferral)
  const referralFormData = useSendReferralStore((state) => state.referralFormData)
  const { data: currentUser } = useMyUserData()
  const eventStore = useEventStore()
  const sessionId = eventStore.sessionId
  const flowId = eventStore.flowId
  const [showOptional, setShowOptional] = React.useState(false)

  const form = useForm<ContractorFormData>({
    resolver: zodResolver(contractorSchema),
    mode: 'onSubmit',
    defaultValues: {
      projectType: referralFormData.projectType || selectedReferral?.projectType || undefined,
      propertyType: referralFormData.propertyType || selectedReferral?.propertyType || undefined,
      budget: {
        min: referralFormData.budget?.min || selectedReferral?.budget?.min || 100000,
        max: referralFormData.budget?.max || selectedReferral?.budget?.max || 450000,
      },
      squareFootageEstimate:
        referralFormData.squareFootageEstimate || selectedReferral?.squareFootageEstimate || undefined,
      descriptionOfWork: referralFormData.descriptionOfWork || selectedReferral?.descriptionOfWork || undefined,
      materialPreferences: referralFormData.materialPreferences || selectedReferral?.materialPreferences || undefined,
      timeframe: referralFormData.timeframe || selectedReferral?.timeframe || undefined,
    },
  })

  const onSubmit = async (data: ContractorFormData) => {
    if (currentUser) {
      sendEvent({
        userId: currentUser.id,
        eventType: 'flow_step',
        info: {
          sessionId,
          flowType: 'send_referral',
          flowStep: 'submit_contractor_info',
          flowId,
          ...data,
        },
      })
    }

    handleNext(data)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="mb-24 space-y-8">
        <SelectFormField
          control={form.control}
          name="projectType"
          label="Project Type"
          options={projectTypeMap}
          required
        />
        <SelectFormField
          control={form.control}
          name="propertyType"
          label="Property Type"
          options={propertyTypeMap}
          required
        />
        <PriceRangeFormField control={form.control} name="budget" label="Budget Range" required />

        <div className="flex w-full justify-center">
          <Button
            variant="link"
            type="button"
            onClick={() => setShowOptional(!showOptional)}
            className="flex items-center justify-center gap-2"
          >
            {showOptional ? <ChevronsUp size={20} /> : <ChevronsDown size={20} />} Optional Info{' '}
            {showOptional ? <ChevronsUp size={20} /> : <ChevronsDown size={20} />}
          </Button>
        </div>
        {showOptional && (
          <>
            <InputFormField
              control={form.control}
              name="squareFootageEstimate"
              label="Square Footage"
              placeholder="Enter"
              floatingLabel={false}
              type="number"
              max={120000}
              fieldClassName="bg-accent"
            />
            <TextareaFormField
              control={form.control}
              name="descriptionOfWork"
              label="Brief Description of the Work Needed"
              description="e.g., 'Kitchen renovation needed', 'Roof repair'"
              maxLength={1000}
              minRows={2}
            />
            <TextareaFormField
              control={form.control}
              name="materialPreferences"
              label="Material preferences"
              description="e.g., 'Eco-friendly materials', 'High-end finishes'"
              maxLength={1000}
              minRows={2}
            />
            <SelectFormField control={form.control} name="timeframe" label="Desired timeline" options={timeframeMap} />
          </>
        )}

        <FixedFooter className="fixed inset-x-0">
          <div className="flex w-full gap-3">
            <Button variant="link" type="button" onClick={handleBack} className="w-full">
              Previous
            </Button>
            <Button type="submit" className="w-full">
              Next
            </Button>
          </div>
        </FixedFooter>
      </form>
    </Form>
  )
}
